import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import OfflineIndicator from "@/components/OfflineIndicator";
import OperatorIdentity from "@/components/OperatorIdentity";
import { openCookiePreferences } from "@/components/CookieBanner";
import { LayoutDashboard, LogOut, Menu, X, Mail } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import InstallAppButton from "@/components/InstallAppButton";
import { BrandLogo } from "@/components/BrandLogo";
import { useT } from "@/i18n/I18nProvider";
import { useAuth } from "@/lib/auth";
import { useOperatorContacts } from "@/lib/operatorContacts";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

function dashboardPathFor(user) {
  const at = (user && (user.account_type || user.role)) || "";
  if (at === "platform_admin") return "/admin";
  if (at === "promoter")       return "/promoter";
  if (at === "customer")       return "/portal";
  if (at === "business_owner" || at === "employee" || at === "owner") return "/app";
  return null;
}

/**
 * Single source of truth for the public nav: used by both the desktop bar
 * and the mobile drawer, and reused by anything that embeds the real
 * header (e.g. the homepage walkthrough's "you're on the homepage right
 * now" step).
 */
function usePublicNavLinks() {
  const { t } = useT();
  const { pathname } = useLocation();
  const isBusinessSurface = pathname.startsWith("/businesses") || pathname === "/features/businesses";

  // The business surface gets its own nav rather than the renter one with
  // two labels swapped: renter items like Find Booking / Request
  // Verification don't belong in front of a business owner. See the
  // 2026-08-27 business homepage brief.
  if (isBusinessSurface) {
    return [
      { to: "/features/businesses", label: "Features",                 testId: "nav-biz-features", desktop: "sm" },
      { to: "/businesses#groups",  label: t("biz.nav.product"),        testId: "nav-biz-product",  desktop: "sm" },
      { to: "/businesses#demo",    label: t("biz.nav.how_it_works"),   testId: "nav-biz-how",      desktop: "sm" },
      { to: "/businesses/pricing", label: t("biz.nav.plans"),          testId: "nav-biz-plans",    desktop: "sm" },
      { to: "/businesses/academy", label: t("biz.nav.guides"),         testId: "nav-biz-guides",   desktop: "md" },
      { to: "/support",            label: t("biz.nav.help"),           testId: "nav-biz-help",     desktop: "md" },
    ];
  }

  return [
    { to: "/features/renters",     label: "Features",                  testId: "nav-renter-features",       desktop: "sm" },
    { to: "/rentals",              label: "Discover Rentals",          testId: "nav-discover-rentals",      desktop: "sm" },
    { to: "/academy",              label: "Guides & Games",              testId: "nav-academy",                desktop: "md" },
    { to: "/find-booking",         label: t("public.nav.find_booking"), testId: "nav-find-booking",          desktop: "sm" },
    { to: "/request-confirmation", label: t("public.nav.request_verification"), testId: "nav-request-confirmation", desktop: "md" },
    { to: "/pricing",              label: "Pricing",                    testId: "nav-pricing",               desktop: "sm" },
    { to: "/about",                label: "About",                      testId: "nav-about",                 desktop: "md" },
    { to: "/faq",                  label: t("public.nav.faq"),          testId: "nav-faq",                   desktop: "md" },
  ];
}

/**
 * Real public header, extracted so it can be embedded verbatim elsewhere
 * (the homepage walkthrough's "start on the homepage" step) without
 * duplicating markup or risking a nested <Outlet/> render loop. Markup and
 * behavior are unchanged from before the extraction: PublicLayout below
 * just calls this instead of inlining it, owning the drawer-open state and
 * the accessible-overlay refs it always owned.
 */
export function PublicHeader({ drawerOpen, triggerRef, onOpenDrawer, spotlightTestId = null }) {
  const { t } = useT();
  const { user, logout } = useAuth();
  const navLinks = usePublicNavLinks();
  const dashTo = dashboardPathFor(user);
  const currentPath = useLocation().pathname;
  const isBusinessSurface = currentPath.startsWith("/businesses") || currentPath === "/features/businesses";
  const audienceCtaTo = isBusinessSurface ? "/" : "/businesses";
  const audienceCtaLabel = isBusinessSurface
    ? t("public.nav.for_renters")
    : t("public.nav.for_businesses");
  const audienceCtaTestId = isBusinessSurface ? "nav-for-renters" : "nav-for-businesses";

  // On phones the desktop nav-discover-rentals link isn't rendered at all -
  // the hamburger is the real way there, so spotlighting "the way to
  // Discover Rentals" means highlighting whichever of the two is actually
  // on screen, not just the literal testId asked for.
  const spotlightClass = (testId) => {
    const isTarget =
      testId === spotlightTestId ||
      (spotlightTestId === "nav-discover-rentals" && testId === "public-mobile-menu-toggle");
    return isTarget
      ? " ring-2 ring-rose-500 ring-offset-2 rounded-md animate-pulse motion-reduce:animate-none"
      : "";
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16 gap-2">
        {/* Logo */}
        <Link to="/" data-testid="public-logo-link" className="flex items-center gap-2 min-w-0">
          <BrandLogo
            variant="full"
            className="h-9 sm:h-11 w-auto"
            alt="ConfirmPH"
            testId="public-brand-logo"
          />
        </Link>

        {/* Desktop nav (≥1024px) */}
        <nav className="hidden lg:flex items-center gap-1 lg:gap-2 text-sm">
          {navLinks.map((l) => (
            <Link key={l.testId} to={l.to} data-testid={l.testId}
                  className={`${l.desktop === "md" ? "hidden 2xl:inline-block" : "inline-block"} px-2 lg:px-3 py-2 rounded-md hover:bg-slate-100 text-xs lg:text-sm whitespace-nowrap${spotlightClass(l.testId)}`}>
              {l.label}
            </Link>
          ))}
          <LanguageSwitcher />
          {user && dashTo ? (
            <>
              <Link to={dashTo} data-testid="nav-go-to-dashboard"
                    className="ml-1 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white font-medium text-xs lg:text-sm whitespace-nowrap hover:bg-[#143027]">
                <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <button onClick={logout} data-testid="nav-logout"
                      aria-label="Sign out"
                      className="inline-flex items-center gap-1 px-2 py-2 rounded-md hover:bg-slate-100 text-xs text-slate-600">
                <LogOut className="w-3.5 h-3.5" /> <span className="hidden 2xl:inline">Sign out</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login"
                    className="px-3 py-2 rounded-md hover:bg-slate-100 text-xs lg:text-sm whitespace-nowrap">{t("public.nav.login")}</Link>
              <Link to={audienceCtaTo} data-testid={audienceCtaTestId}
                    className="ml-1 inline-flex items-center px-3 lg:px-4 py-2 rounded-lg btn-brand font-medium text-xs lg:text-sm whitespace-nowrap">
                {audienceCtaLabel}
              </Link>
            </>
          )}
        </nav>

        {/* Mobile + tablet (<1024px): tiny primary CTA + hamburger */}
        <div className="flex lg:hidden items-center gap-1.5">
          {user && dashTo ? (
            <Link to={dashTo} data-testid="nav-go-to-dashboard-mobile"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1B4332] text-white font-medium text-xs whitespace-nowrap">
              <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
            </Link>
          ) : (
            <Link to={audienceCtaTo} data-testid={`${audienceCtaTestId}-mobile`}
                  className="inline-flex items-center px-2.5 py-1.5 rounded-lg btn-brand font-medium text-xs whitespace-nowrap">
              {audienceCtaLabel}
            </Link>
          )}
          <button data-testid="public-mobile-menu-toggle"
                  ref={triggerRef}
                  aria-label="Open main navigation"
                  aria-expanded={drawerOpen}
                  aria-controls="public-mobile-navigation"
                  onClick={onOpenDrawer}
                  className={`min-w-11 min-h-11 inline-flex items-center justify-center rounded-md hover:bg-slate-100 border border-slate-200${spotlightClass("public-mobile-menu-toggle")}`}>
            <Menu className="w-5 h-5 text-slate-700" aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
}

/**
 * Real mobile/tablet navigation drawer, extracted the same way PublicHeader
 * was, so it can be embedded verbatim wherever the real hamburger needs to
 * open the real drawer (the homepage walkthrough's "open the menu, then tap
 * Discover Rentals" step) without duplicating markup. Behavior and markup
 * are unchanged from before the extraction.
 */
export function PublicMobileDrawer({ drawerOpen, onClose, panelRef }) {
  const { t } = useT();
  const { user, logout } = useAuth();
  const navLinks = usePublicNavLinks();
  const dashTo = dashboardPathFor(user);

  if (!drawerOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            aria-hidden="true"
            data-overlay-backdrop
            onClick={onClose}
            data-testid="public-drawer-backdrop" />
      <aside className="fixed inset-y-0 right-0 w-80 max-w-[90vw] bg-white z-50 lg:hidden overflow-y-auto shadow-2xl"
              ref={panelRef}
              id="public-mobile-navigation"
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
              tabIndex={-1}
              data-testid="public-mobile-drawer">
        <div className="flex items-center justify-between px-4 h-14 border-b border-slate-200">
          <BrandLogo
            variant="full"
            className="h-9 w-auto"
            alt="ConfirmPH"
            testId="public-drawer-brand-logo"
          />
          <button data-testid="public-drawer-close"
                  aria-label="Close menu"
                  onClick={onClose}
                  className="min-w-11 min-h-11 inline-flex items-center justify-center rounded-md hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="p-3 space-y-0.5">
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Browse</div>
          {navLinks.map((l) => (
            <Link key={l.testId} to={l.to} data-testid={`drawer-${l.testId}`}
                  onClick={onClose}
                  className="block px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-100">
              {l.label}
            </Link>
          ))}

          <div className="px-3 pt-4 pb-2 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Your account</div>
          {user && dashTo ? (
            <>
              <Link to={dashTo} onClick={onClose} data-testid="drawer-nav-go-to-dashboard"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm bg-[#1B4332] text-white">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={() => { onClose(); logout(); }}
                      data-testid="drawer-nav-logout"
                      className="w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-100">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={onClose} data-testid="drawer-nav-login"
                    className="block px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-100">
                {t("public.nav.login")}
              </Link>
              <Link to="/register" onClick={onClose} data-testid="drawer-nav-register"
                    className="block px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-100">
                Create free account
              </Link>
              <Link to="/businesses" onClick={onClose} data-testid="drawer-nav-for-businesses"
                    className="block px-3 py-2.5 rounded-lg text-sm bg-[#1B4332] text-white text-center font-medium">
                {t("public.nav.for_businesses")}
              </Link>
            </>
          )}

          <div className="px-3 pt-4 pb-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Language</span>
            <LanguageSwitcher />
          </div>

          <div className="px-3 pt-3 pb-2 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Get the app</div>
          <div className="px-3 pb-3">
            <InstallAppButton
              variant="secondary"
              size="md"
              className="w-full justify-center"
              testId="drawer-install-app-btn"
              label="Install ConfirmPH"
            />
          </div>
        </nav>
      </aside>
    </>
  );
}

export default function PublicLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(drawerOpen, () => setDrawerOpen(false));

  function closeDrawer() { setDrawerOpen(false); }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900">
      <OfflineIndicator />
      <PublicHeader drawerOpen={drawerOpen} triggerRef={triggerRef} onOpenDrawer={() => setDrawerOpen(true)} />
      <PublicMobileDrawer drawerOpen={drawerOpen} onClose={closeDrawer} panelRef={panelRef} />
      <main>
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}

export function PublicFooter() {
  const year = new Date().getFullYear();
  const contacts = useOperatorContacts();
  return (
    <footer className="border-t border-slate-200 mt-20 bg-white" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-4 gap-8 text-sm">
        <div className="sm:col-span-1">
          <BrandLogo
            variant="full"
            className="h-10 w-auto"
            alt="ConfirmPH"
            testId="public-footer-brand-logo"
          />
          <p className="text-slate-600 mt-3 leading-relaxed">
            Booking confirmation and rental operations software for Philippine rental businesses.
          </p>
          <a
            href={`mailto:${contacts.support}`}
            data-testid="footer-contact-email"
            className="mt-2 inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 underline"
          >
            <Mail className="w-3 h-3" /> {contacts.support}
          </a>
        </div>
        <FooterCol title="For Customers" links={[
          { to: "/features",             label: "All Features" },
          { to: "/features/renters",     label: "Features for Renters" },
          { to: "/rentals",              label: "Discover Rentals" },
          { to: "/academy",              label: "Renter Guides & Games" },
          { to: "/academy/checklists/renter-safety-checklist", label: "Renter Safety Checklist" },
          { to: "/find-booking",         label: "Find My Booking" },
          { to: "/request-confirmation", label: "Request Confirmation" },
          { to: "/pricing",              label: "Customer pricing" },
          { to: "/about",                label: "About ConfirmPH" },
          { to: "/customer-terms",       label: "Customer Terms" },
          { to: "/faq",                  label: "FAQ" },
          { to: "/support",              label: "Support" },
        ]} testGroup="footer-customers" />
        <FooterCol title="For Businesses" links={[
          { to: "/features/businesses",  label: "Features for Businesses" },
          { to: "/businesses",            label: "For Businesses" },
          { to: "/businesses/intake",     label: "How Booking Intake Works" },
          { to: "/businesses/about",      label: "About ConfirmPH for Businesses" },
          { to: "/businesses/academy",    label: "Rental Business Guides & Games" },
          { to: "/businesses/pricing",    label: "Business plans" },
          { to: "/register?as=business",  label: "Create free account" },
          { to: "/login?as=business",     label: "Login" },
          { to: "/business-terms",        label: "Business Terms" },
          { to: "/trust-safety",          label: "Trust & Safety" },
        ]} testGroup="footer-business" />
        <FooterCol title="Legal" links={[
          { to: "/legal",          label: "Policy Centre" },
          { to: "/what-we-verify", label: "What ConfirmPH verifies" },
          { to: "/report",         label: "Report a problem" },
          { to: "/security",       label: "Security" },
          { to: "/terms",          label: "Terms of Service" },
          { to: "/privacy",        label: "Privacy Policy" },
          { to: "/cookies",        label: "Cookie Policy" },
          { to: "/refund-policy",  label: "Refund & Billing" },
          { to: "/disclaimer",     label: "Disclaimer" },
          { to: "/acceptable-use", label: "Acceptable Use" },
          { to: "/reviews-policy", label: "Reviews Policy" },
          { to: "/data-deletion",  label: "Data Deletion" },
          { to: "/contact",        label: "Contact" },
        ]} testGroup="footer-legal" />
        <FooterCol title="Careers" links={[
          { to: "/careers/sales-agent", label: "Become a ConfirmPH Sales Agent" },
        ]} testGroup="footer-careers" />
      </div>
      <div className="border-t border-slate-200 py-4 px-4 text-center text-xs text-slate-500 space-y-1"
           data-testid="footer-bottom">
        <div>© {year} ConfirmPH. Built for the Philippines.</div>
        <OperatorIdentity />
        <div>
          <Link to="/disclaimer" className="underline hover:text-slate-700">
            ConfirmPH does not own or operate the rental businesses shown.
          </Link>
        </div>
        <div>
          <button type="button" onClick={openCookiePreferences}
                  data-testid="footer-cookie-preferences"
                  className="underline hover:text-slate-700">
            Cookie preferences
          </button>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links, testGroup }) {
  return (
    <div data-testid={testGroup}>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{title}</div>
      <ul className="mt-3 space-y-1.5">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} data-testid={`${testGroup}-${l.to.replace(/^\//, "").replace(/[?=&]/g, "-")}`}
                  className="hover:underline text-slate-700">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
